<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.kill_list')" active></b-tab>
        </b-tabs>
      </div>
      <div class="tab-content">
        <div class="tab-content-inner">
          <!-- Search and Filters Section -->
          <div class="filters-section">
            <div class="search-container">
              <label class="search-label">{{ $t("globals.search") }}</label>
              <b-form-input
                v-model="searchFilter"
                class="search-input"
                :placeholder="$t('globals.reference') + '...'"
                :disabled="getKillListLoading"
              />
            </div>

            <div class="filter-group">
              <div class="filter-item">
                <label class="filter-label">{{ $t("globals.status") }}</label>
                <b-form-select
                  v-model="statusFilter"
                  :options="statusFilterOptions"
                  class="filter-select"
                />
              </div>
            </div>

            <div class="action-buttons">
              <button
                class="reload-btn"
                @click="resetReload"
                :disabled="getKillListLoading"
                v-b-tooltip.hover
                :title="$t('globals.clear_filters')"
              >
                <icon name="sync-alt" :spin="getKillListLoading"></icon>
              </button>
            </div>
          </div>

          <!-- Table -->
          <div class="table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    :key="field.key"
                    :class="{ sortable: field.sortable }"
                    @click="field.sortable && handleSort(field.key)"
                  >
                    {{ field.label }}
                    <span v-if="sortBy === field.key" class="sort-icon">
                      {{ sortDesc ? '▼' : '▲' }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in paginatedItems"
                  :key="item.id"
                  class="table-row"
                >
                  <td>{{ item.id }}</td>
                  <td>{{ item.reference }}</td>
                  <td>{{ formatDate(item.created_at) }}</td>
                  <td>{{ formatDate(item.updated_at) }}</td>
                  <td>{{ item.process_type || '-' }}</td>
                  <td>
                    <span class="status-badge" :class="getStatusClass(item.status)">
                      {{ item.status }}
                    </span>
                  </td>
                  <td style="text-align: center;">
                    <button
                      v-if="item.status === 'pending' || item.status === 'failed'"
                      @click="terminateDevice(item)"
                      :disabled="item.terminating"
                      class="terminate-btn"
                    >
                      <b-spinner v-if="item.terminating" small class="mr-1"></b-spinner>
                      {{ item.terminating ? 'TERMINATING...' : 'TERMINATE IN TELE2' }}
                    </button>
                    <span v-else style="color: #6c757d;">-</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="getKillListLoading" class="loading-state">
              <div class="spinner"></div>
              <p>{{ $t("globals.loading") }}...</p>
            </div>
            <div v-else-if="filteredItems.length === 0" class="empty-state">
              <p>{{ $t("globals.no_results") }}</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-section">
            <div class="controls-left">
              <span class="summary-label">{{ $t("globals.per_page") }}</span>
              <b-form-select
                v-model="perPage"
                :options="perPageOptions"
                class="per-page-select"
              />
              <span class="summary-label" style="margin-left: 1rem;">
                {{ $t("globals.showing") }}
                {{ ((currentPage - 1) * perPage) + 1 }}-{{ Math.min(currentPage * perPage, filteredItems.length) }}
                {{ $t("globals.of") }} {{ filteredItems.length }}
              </span>
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="currentPage = 1"
              >
                &lt;&lt;
              </button>
              <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="currentPage--"
              >
                &lt;
              </button>
              <span style="padding: 0 1rem; color: #6c757d; font-size: 0.875rem;">
                {{ $t("globals.page") }} {{ currentPage }} {{ $t("globals.of") }} {{ totalPages }}
              </span>
              <button
                class="page-btn"
                :disabled="currentPage >= totalPages"
                @click="currentPage++"
              >
                &gt;
              </button>
              <button
                class="page-btn"
                :disabled="currentPage >= totalPages"
                @click="currentPage = totalPages"
              >
                &gt;&gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
  },
  data() {
    return {
      table: {
        offset: 0,
        limit: 99999,
        order_by: "created_at ASC"
      },
      statusFilter: 'pending',
      searchFilter: '',
      currentPage: 1,
      perPage: 20,
      sortBy: "created_at",
      sortDesc: true,
      perPageOptions: [
        { text: "20", value: 20 },
        { text: "40", value: 40 },
        { text: "60", value: 60 }
      ],
      statusFilterOptions: [
        { value: '', text: this.$t('globals.all') },
        { value: 'pending', text: 'Pending' },
        { value: 'terminated', text: 'Terminated' },
        { value: 'failed', text: 'Failed' }
      ],
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true
        },
        {
          key: "reference",
          label: this.$t("globals.reference"),
          sortable: true
        },
        {
          key: "created_at",
          label: this.$t("globals.created_at"),
          sortable: true
        },
        {
          key: "updated_at",
          label: this.$t("globals.updated_at"),
          sortable: true
        },
        {
          key: "process_type",
          label: this.$t("globals.process_type"),
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "actions",
          label: this.$t("globals.actions"),
          sortable: false
        }
      ]
    };
  },
  computed: {
    ...mapGetters("kill_list", [
      "getKillList",
      "getKillListStatuses",
      "getKillListLoading",
    ]),
    filteredItems() {
      let items = [...this.getKillList];

      // Apply status filter
      if (this.statusFilter) {
        items = items.filter(item => item.status === this.statusFilter);
      }

      // Apply search filter
      if (this.searchFilter) {
        const searchTerm = this.searchFilter.toLowerCase();
        items = items.filter(item => {
          const reference = (item.reference || '').toLowerCase();
          return reference.includes(searchTerm);
        });
      }

      return this.sortItems(items);
    },
    sortedItems() {
      return this.sortItems([...this.filteredItems]);
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredItems.length / this.perPage) || 1;
    }
  },
  methods: {
    ...mapActions("kill_list", ["fetchKillList", "updateStatus"]),
    ...mapActions("axios_mod", ["simpleFetch"]),
    sortItems(items) {
      if (!this.sortBy) {
        return items;
      }

      return items.sort((a, b) => {
        const aVal = a[this.sortBy];
        const bVal = b[this.sortBy];

        if (aVal === bVal) return 0;

        let result = 0;
        if (aVal === null || aVal === undefined) {
          result = 1;
        } else if (bVal === null || bVal === undefined) {
          result = -1;
        } else {
          result = aVal < bVal ? -1 : 1;
        }

        return this.sortDesc ? -result : result;
      });
    },
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';

      if (dateStr.includes('/')) {
        const [day, month, year] = dateStr.split('/');
        return `${day}-${month}-${year}`;
      }

      const date = new Date(dateStr);
      if (isNaN(date)) return '';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    clearFilters() {
      this.statusFilter = '';
      this.searchFilter = '';
      this.currentPage = 1;
    },
    resetReload() {
      this.statusFilter = 'pending';
      this.searchFilter = '';
      this.currentPage = 1;
      this.sortBy = "created_at";
      this.sortDesc = true;
      this.fetchKillList({ limit: this.table.limit, offset: this.table.offset });
    },
    getStatusClass(status) {
      switch (status) {
        case 'pending':
          return 'status-pending';
        case 'terminated':
          return 'status-completed';
        case 'failed':
          return 'status-cancelled';
        default:
          return 'status-new';
      }
    },
    async terminateDevice(item) {
      if (!confirm(`Are you sure you want to terminate device ${item.reference}? This action cannot be undone.`)) {
        return;
      }

      this.$set(item, 'terminating', true);

      try {
        const data = {
          query: `
            mutation {
              terminateKillListDevice(id: ${item.id})
            }
          `
        };

        const response = await this.simpleFetch({ data, this_ref: this });

        if (response && response.terminateKillListDevice) {
          this.$bvToast.toast(`Device ${item.reference} terminated successfully.`, {
            title: 'Success',
            variant: 'success',
            solid: true
          });

          this.fetchKillList({
            limit: this.table.limit,
            offset: this.table.offset
          });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Error terminating device:', error);
        this.$bvToast.toast(`Failed to terminate device ${item.reference}: ${error.message}`, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.$set(item, 'terminating', false);
      }
    }
  },
  watch: {
    statusFilter() {
      this.currentPage = 1;
    },
    searchFilter() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchKillList({ limit: this.table.limit, offset: this.table.offset });
  },
  created() {
    // if (!this.getUserBrandPermissions.kill_list_index) {
    //   this.$router.push({ name: "dashboard" });
    // }
  }
};
</script>
